/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { v4 as uid } from "uuid";
import {
  CourseEnrollment,
  // Post, // Removed Post import
  QuizAttempt,
  Notification,
  UserProfile,
  UserActivity,
  UserAchievement,
  UserPreferences,
  UserRole,
  CourseEnrollmentStatus,
  UserLearningProgressData,
  AchievementCriteria,
} from "../types.ts";
import {
  updateLastActiveApi,
  updateUserInterestsApi,
  updateUserProfileApi,
  changeUserRoleApi,
  fetchUserProfileApi,
  createUserProfileApi,
} from "../apis/user";
import { logUserActivityApi, fetchUserActivitiesApi } from "../apis/activity";
import { recordStudyTimeApi } from "../apis/user";
import {
  createNotificationApi,
  fetchUserNotificationsApi,
  markNotificationAsReadApi,
  deleteNotificationApi,
} from "../apis/notification";
import {
  enrollInCourseApi,
  updateCourseEnrollmentApi,
  unenrollFromCourseApi,
  fetchEnrolledCoursesApi,
} from "../apis/course";

import {
  recordQuizAttemptApi,
  deleteQuizAttemptApi,
  fetchQuizAttemptsApi,
} from "../apis/quiz";
// import { fetchUserPostsApi } from "../apis/post"; // Removed
import {
  addUserAchievementApi,
  fetchUserAchievementsApi,
  deleteUserAchievementApi,
} from "../apis/achievement";
import { updatePreferencesApi } from "../apis/preferences";
import { enrollmentCourseId } from "../lib/enrollment";
import { useMemo } from "react";

interface UserState {
  authUser: UserProfile | null;
  profile: Partial<UserProfile> | UserProfile | null;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;

  enrolledCourses: CourseEnrollment[];
  createdContent: any;
  quizAttempts: QuizAttempt[];

  progress: UserLearningProgressData;

  activityTimeline: UserActivity[];
  notifications: Notification[];
  achievements: UserAchievement[];
  preferences: UserPreferences;

  permissions: {
    canCreateCourses: boolean;
    canCreateQuizzes: boolean;
    canUploadMaterials: boolean;
    canManageUsers: boolean;
    canModerateContent: boolean;
    canAccessAdminPanel: boolean;
    canAccessAnalytics: boolean;
    canEditProfile: boolean;
    canDeleteAccount: boolean;
    canViewAnalytics: boolean;
    canCreateContent: boolean;
    canManageContent: boolean;
    canApproveContent: boolean;
  };

  isAuthenticated: boolean;

  // Synchronous actions
  setAuthUser: (user: UserProfile | null) => void; // Changed to UserProfile
  setProfile: (profile: Partial<UserProfile> | null) => void;
  setError: (error: string | null) => void;
  setLoading: (isLoading: boolean) => void;
  resetStore: () => void;
  clearErrors: () => void;

  // Core user management
  initializeUser: (user: UserProfile | null) => Promise<void>; // Changed to UserProfile
  updateUserInterests: (interests: string[]) => Promise<void>;
  updateProfile: (profileUpdate: Partial<UserProfile>) => Promise<void>;
  updateUserRole: (userId: string, newRole: UserRole) => Promise<void>;

  // Activity and notifications
  logActivity: (activity: Omit<UserActivity, "id">) => Promise<void>;
  loadUserActivities: () => Promise<void>;
  sendNotification: (notification: Omit<Notification, "id">) => Promise<void>;
  loadNotifications: () => Promise<void>;
  markNotificationRead: (notificationId: string) => Promise<void>;
  removeNotification: (notificationId: string) => Promise<void>;

  // Course enrollment management
  enrollUserInCourse: (courseId: string, paymentReference?: string, enrollmentType?: 'paid' | 'scholarship' | 'free') => Promise<void>;
  updateCourseProgress: (
    courseId: string,
    lessonId: string,
    progress: number
  ) => Promise<void>;
  updateEnrollmentProgress: (
    enrollmentId: string,
    updates: Partial<CourseEnrollment>
  ) => Promise<void>;
  unenrollFromCourse: (enrollmentId: string) => Promise<void>;
  loadEnrolledCourses: () => Promise<void>;
  refreshEnrolledCourses: () => Promise<void>;

  // Quiz management
  /**
   * Returns the attempt as the server graded it. The caller needs that: the
   * browser's own pass/fail was computed by comparing a percentage against a raw
   * question count, so it passed nearly everything.
   */
  addQuizAttempt: (
    attemptData: Omit<QuizAttempt, "id" | "startedAt">
  ) => Promise<QuizAttempt | null>;
  removeQuizAttempt: (attemptId: string) => Promise<void>;
  loadQuizAttempts: () => Promise<void>;

  // Content management
  loadUserCreatedContent: () => Promise<void>;

  // Achievement management
  addAchievement: (
    achievement: Omit<UserAchievement, "id" | "earnedAt">
  ) => Promise<void>;
  loadAchievements: () => Promise<void>;
  removeAchievement: (achievementId: string) => Promise<void>;

  // Progress and stats
  recordStudyTime: (minutes: number) => Promise<void>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;

}

/**
 * Advisory UI flags only — NOT an access-control decision.
 *
 * This is a second, hand-maintained copy of the permission matrix and it had
 * already drifted from the first: `superAdmin` was granted strictly less than
 * `admin` here, and the `editor` case keyed off a role the API has never
 * accepted. Both are the predictable result of describing the same rules twice.
 *
 * The authority is `config/permissions.ts` on the server, surfaced to the client
 * by `useCan()`. Gate on that. This function survives only because the store
 * needs a synchronous initial value before that request has resolved, so it is
 * deliberately conservative: it answers for the coarse role bands and leaves the
 * departmental roles to the real check.
 */
const getRolePermissions = (role: UserRole) => {
  const basePermissions = {
    canCreateCourses: false,
    canCreateQuizzes: false,
    canUploadMaterials: false,
    canManageUsers: false,
    canModerateContent: false,
    canAccessAdminPanel: false,
    canAccessAnalytics: false,
    canEditProfile: true, // All users can edit their profile
    canDeleteAccount: true, // All users can delete their account
    canViewAnalytics: false,
    canCreateContent: false,
    canManageContent: false,
    canApproveContent: false,
  };

  switch (role) {
    // Both hold every permission in the catalogue; the difference between them
    // is who they may act *on*, which is not expressible as a capability flag.
    case UserRole.ADMIN:
    case UserRole.SUPER_ADMIN:
      return {
        ...basePermissions,
        canCreateCourses: true,
        canCreateQuizzes: true,
        canUploadMaterials: true,
        canManageUsers: true,
        canModerateContent: true,
        canAccessAdminPanel: true,
        canAccessAnalytics: true,
        canViewAnalytics: true,
        canCreateContent: true,
        canManageContent: true,
        canApproveContent: true,
      };

    case UserRole.CONTENT_MANAGER:
      return {
        ...basePermissions,
        canCreateCourses: true,
        canCreateQuizzes: true,
        canUploadMaterials: true,
        canAccessAdminPanel: true,
        canCreateContent: true,
        canManageContent: true,
      };

    case UserRole.MARKETING:
      return {
        ...basePermissions,
        canUploadMaterials: true,
        canAccessAdminPanel: true,
        canCreateContent: true,
        canManageContent: true,
        canModerateContent: true,
      };

    case UserRole.SUPPORT:
      return { ...basePermissions, canManageUsers: true, canAccessAdminPanel: true };

    case UserRole.ADMISSIONS:
      return { ...basePermissions, canAccessAdminPanel: true, canApproveContent: true };

    case UserRole.FINANCE:
      return {
        ...basePermissions,
        canAccessAdminPanel: true,
        canAccessAnalytics: true,
        canViewAnalytics: true,
      };

    // Sees the panel, changes nothing.
    case UserRole.AUDITOR:
      return {
        ...basePermissions,
        canAccessAdminPanel: true,
        canAccessAnalytics: true,
        canViewAnalytics: true,
      };

    case UserRole.INSTRUCTOR:
    case UserRole.READER: // Default role
    default:
      return basePermissions;
  }
};

// Default user preferences
export const defaultPreferences: UserPreferences = {
  theme: "system",
  emailNotifications: true,
  pushNotifications: true,
  newsletterSubscription: false,
  contentLanguage: "en",
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  privacySettings: {
    showProfile: true,
    showActivity: true,
    showAchievements: true,
    allowMessages: true,
  },
};

// Initial state
const initialState = {
  authUser: null,
  profile: null,
  isInitialized: false,
  isLoading: true,
  error: null,
  enrolledCourses: [],
  createdContent: {}, // Removed posts: []
  quizAttempts: [],
  progress: {
    coursesCompleted: 0,
    coursesInProgress: 0,
    quizzesTaken: 0,
    averageQuizScore: 0,
    studyHours: 0,
    streakDays: 0,
    courses: {},
    quizzes: {},
  },
  activityTimeline: [],
  notifications: [],
  achievements: [],
  preferences: defaultPreferences,
  permissions: getRolePermissions(UserRole.READER),
  isAuthenticated: false,
  _unsubscribeFromNotifications: null,
};

/**
 * Whether two enrolment rows say the same thing about the same course.
 *
 * Only the fields anything renders off: the identity, and the three marks that
 * decide what a card or a lesson list shows. A deep compare of the populated
 * course document would be the wrong price to pay on every refresh.
 */
const sameEnrollment = (a: CourseEnrollment, b: CourseEnrollment): boolean =>
  a?._id === b?._id &&
  enrollmentCourseId(a) === enrollmentCourseId(b) &&
  (a?.progress ?? 0) === (b?.progress ?? 0) &&
  a?.status === b?.status &&
  Boolean(a?.certificateIssued) === Boolean(b?.certificateIssued);

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Synchronous actions
      setAuthUser: (user) => {

        set({
          authUser: user,
          isAuthenticated: !!user,
          isInitialized: true,
        });
      },

      setProfile: (profile) => {
        if (profile) {
          const roleBasedPermissions = getRolePermissions(profile.role as UserRole);
          set({ profile, permissions: roleBasedPermissions, isLoading: false });
        } else {
          set({ profile: null, permissions: getRolePermissions(UserRole.READER), isLoading: false });
        }
      },

      setError: (error) => set({ error }),
      setLoading: (isLoading) => set({ isLoading }),
      clearErrors: () => set({ error: null }),
      resetStore: () => set(initialState),

      // Core user management
      initializeUser: async (user: UserProfile | null) => {
        const previousAuthUser_id = get().authUser?._id;
        const wasInitialized = get().isInitialized;
        const isSoftRefresh = wasInitialized && previousAuthUser_id === user?._id && user !== null;

        if (!isSoftRefresh) {
          console.log("[UserStore] initializeUser: Hard refresh / New user. Setting isLoading: true");
          set({ isLoading: true, error: null });
        } else {
          console.log("[UserStore] initializeUser: Soft refresh for existing user.");
          set({ error: null });
        }

        try {
          if (!user) {
            console.log("[UserStore] initializeUser: User is null. Resetting store.");
            get().resetStore();
            set({ isLoading: false });
            return;
          }

          get().setAuthUser(user);

          let userProfile = await fetchUserProfileApi(user._id);
          if (!userProfile) {
            console.log("[UserStore] initializeUser: No profile found, creating one for user:", user._id);
            userProfile = await createUserProfileApi(user._id, {
              displayName: user.displayName || user.email?.split("@")[0] || "",
              email: user.email || "",
              photoURL: user.photoURL,
            });
          }

          if (userProfile) {
            get().setProfile(userProfile);
            set({ isInitialized: true }); // Set isInitialized here
            await updateLastActiveApi(user._id);

            if (!isSoftRefresh) {
              await get().logActivity({ type: "login", _id: user._id, userId: user._id, timestamp: new Date(), metadata: {} });
            }

            // Load enrolled courses and other data
            await Promise.all([
              get().loadEnrolledCourses(),
              get().loadQuizAttempts(),
              get().loadUserCreatedContent(),
              get().loadUserActivities(),
              get().loadAchievements(),
            ]);

          } else {
            throw new Error("Failed to initialize user profile");
          }
        } catch (err) {
          console.error("[UserStore] initializeUser: Error:", err);
          set({ error: err instanceof Error ? err.message : "Unknown error during initialization" });
        } finally {
          if (!isSoftRefresh) {
            console.log("[UserStore] initializeUser: Setting isLoading: false");
            set({ isLoading: false });
          }
        }
      },

      updateUserInterests: async (interests) => {
        const authUser = get().authUser;
        if (!authUser) {
          set({ error: "User not authenticated" });
          return;
        }
        if (!interests) {
          set({ error: "Interests cannot be empty!" });
          return;
        }

        // Kept so a failed save can put it back — see the catch below.
        const previousProfile = get().profile;

        try {
          set({ isLoading: true });

          // Update profile in state
          const updatedProfile = {
            ...previousProfile,
            interests,
          };

          set({ profile: updatedProfile });

          // Update profile interest in Firestore
          await updateUserInterestsApi(authUser._id, interests);

          // Log profile update activity
          await get().logActivity({
            _id: authUser._id,
            type: "interests_updated",
            userId: authUser._id,
            timestamp: new Date(),
            metadata: {},
          });
        } catch (error) {
          console.error("Error updating interests:", error);
          /**
           * Roll back and rethrow, exactly as `updateProfile` below does.
           *
           * The optimistic `set({ profile: updatedProfile })` above runs before
           * the request, and this block used to swallow the rejection into
           * `error` state that nothing renders. `Onboarding.tsx` awaits this
           * call inside a try/catch that shows "Failed to save your
           * preferences" — unreachable, because the promise resolved. So a
           * failed save showed "Preferences saved successfully!", navigated to
           * the profile, and displayed interests that had never been stored.
           */
          set({
            profile: previousProfile,
            error:
              error instanceof Error
                ? error.message
                : "Unknown error updating interests",
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateProfile: async (profileUpdate) => {
        const authUser = get().authUser;
        const currentProfile = get().profile;

        if (!authUser || !currentProfile) {
          set({ error: "User not authenticated" });
          return;
        }

        try {
          set({ isLoading: true });

          // If role is being updated, update permissions
          if (
            profileUpdate.role &&
            profileUpdate.role !== currentProfile.role
          ) {
            const newPermissions = getRolePermissions(
              profileUpdate.role as UserRole
            );
            set({ permissions: newPermissions });
          }

          // Update profile in state
          const updatedProfile = {
            ...currentProfile,
            ...profileUpdate,
            updatedAt: new Date(),
          };

          set({ profile: updatedProfile });

          // Update profile in Firestore
          await updateUserProfileApi(authUser._id, profileUpdate);

          // Log profile update activity
          await get().logActivity({
            _id: authUser._id,
            type: "profile_updated",
            userId: authUser._id,
            timestamp: new Date(),
            metadata: {},
          });
        } catch (error) {
          console.error("Error updating profile:", error);
          /**
           * Put the profile back, then let the caller know it failed.
           *
           * The optimistic `set({ profile: updatedProfile })` above runs before
           * the request, and this block used to swallow the rejection into
           * `error` state that nothing rendered. The drawer's `await` therefore
           * resolved, it closed itself, and the screen showed the new value —
           * which vanished on the next reload, because it had never been saved.
           * A silent write failure is worse than a loud one: the user believes
           * the change stuck.
           *
           * Rolling back keeps the screen honest, and rethrowing reaches the
           * caller's catch, which already shows a snackbar.
           */
          set({
            profile: currentProfile,
            error:
              error instanceof Error
                ? error.message
                : "Unknown error updating profile",
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateUserRole: async (userId, newRole) => {
        const authUser = get().authUser;
        const currentProfile = get().profile;

        if (!authUser || !currentProfile) {
          set({ error: "User not authenticated" });
          return;
        }

        try {
          set({ isLoading: true });

          // Change user role in Firestore
          const success = await changeUserRoleApi(
            userId,
            newRole
          );

          if (success && userId === authUser._id) {
            // Update permissions and profile for current user
            const newPermissions = getRolePermissions(
              newRole
            );
            set({
              permissions: newPermissions,
              profile: {
                ...currentProfile,
                role: newRole,
              },
            });
          }
        } catch (error) {
          console.error("Error updating user role:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Unknown error updating user role",
          });
        } finally {
          set({ isLoading: false });
        }
      },

      // Activity and notifications
      logActivity: async (activityData) => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {
          const activityId = await logUserActivityApi({ uid: authUser._id, activityData });

          if (activityId) {
            const activity: UserActivity = {
              ...activityData,
              _id: activityId,
            };

            set((state) => ({
              activityTimeline: [activity, ...state.activityTimeline].slice(
                0,
                100
              ), // Keep last 100 activities
            }));
          }
        } catch (error) {
          console.error("Error logging activity:", error);
        }
      },

      loadUserActivities: async () => {
        const authUser = get().authUser;
        if (!authUser?._id) return;

        try {
          // Use the new API function, USER_RECENT_ACTIVITIES_DISPLAY_LIMIT is 20 by default in activity.ts
          const activities = await fetchUserActivitiesApi(authUser._id);
          set({ activityTimeline: activities });
        } catch (error) {
          console.error("Error loading user activities:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to load activities",
          });
        }
      },

      sendNotification: async (notificationData) => {
        const notification = {
          id: uid(),
          read: false,
          ...notificationData,
        } as Notification;

        set((state) => ({
          notifications: [notification, ...state.notifications],
        }));

        await createNotificationApi(notification);
      },

      loadNotifications: async () => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {
          const notifications = await fetchUserNotificationsApi(authUser._id);
          set({ notifications });
        } catch (error) {
          console.error("Error loading notifications:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to load notifications",
          });
        }
      },

      markNotificationRead: async (notificationId) => {
        try {
          await markNotificationAsReadApi(notificationId);

          set((state) => ({
            notifications: state.notifications.map((n) =>
              n._id === notificationId ? { ...n, read: true } : n
            ),
          }));
        } catch (error) {
          console.error("Error marking notification as read:", error);
        }
      },

      removeNotification: async (notificationId) => {
        try {
          await deleteNotificationApi(notificationId);

          set((state) => ({
            notifications: state.notifications.filter(
              (n) => n._id !== notificationId
            ),
          }));
        } catch (error: any) {
          console.error("Error removing notification:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to remove notification",
          });
        }
      },

      // Course enrollment management
      enrollUserInCourse: async (courseId, paymentReference, enrollmentType = 'paid') => {
        const authUser = get().authUser;
        if (!authUser || !authUser._id) {
          set({ error: "User not authenticated" });
          // Potentially throw an error here so the caller can catch it
          throw new Error("User not authenticated");
        }



        try {
          set({ isLoading: true });

          const enrollment = await enrollInCourseApi(authUser._id, courseId, enrollmentType, paymentReference);

          if (enrollment) {

            const newEnrollmentWithCorrectType = { ...enrollment, enrollmentType };

            // Check if this enrollment is new or updated
            const existingEnrollmentIndex = get().enrolledCourses.findIndex(e => e._id === enrollment._id);

            if (existingEnrollmentIndex > -1) {
              // Update existing enrollment in local state
              console.log("[UserStore] enrollUserInCourse: Updating existing enrollment");
              set(state => ({
                enrolledCourses: state.enrolledCourses.map(e =>
                  e._id === newEnrollmentWithCorrectType._id ? { ...e, ...newEnrollmentWithCorrectType } : e
                ),
              }));
            } else {
              // Add new enrollment to local state
              set((state) => ({
                enrolledCourses: [...state.enrolledCourses, newEnrollmentWithCorrectType],
              }));

              // Initialize course progress only for new enrollments
              const courseIdString = typeof newEnrollmentWithCorrectType.courseId === 'string'
                ? newEnrollmentWithCorrectType.courseId
                : (newEnrollmentWithCorrectType.courseId as any)._id;

              set((state) => ({
                progress: {
                  ...state.progress,
                  courses: {
                    ...state.progress.courses,
                    [courseIdString]: {
                      progress: 0,
                      lastAccessed: new Date(),
                      completedLessons: [],
                      quizScores: {},
                    },
                  },
                },
              }));
            }

            // Log course enrollment activity
            const courseIdForLog = typeof newEnrollmentWithCorrectType.courseId === 'string'
              ? newEnrollmentWithCorrectType.courseId
              : (newEnrollmentWithCorrectType.courseId as any)._id;

            await get().logActivity({
              type: "course_enrolled",
              _id: authUser._id,
              userId: authUser._id,
              timestamp: new Date(),
              metadata: {
                courseId: courseIdForLog,
                paymentReference: paymentReference, // Will be undefined for free/scholarship
                enrollmentType: newEnrollmentWithCorrectType.enrollmentType,
              },
            });
          } else {
            // If enrollment is null, it means API call failed or returned null
            throw new Error("Enrollment process failed at the API level.");
          }
        } catch (error) {
          console.error("Error enrolling in course:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Unknown error enrolling in course",
          });
          // Re-throw the error so the calling component (e.g., CoursesPage) can catch it
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateCourseProgress: async (courseId, lessonId, progress) => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {
          const state = get();
          const courseProgress = state.progress.courses[courseId] || {
            progress: 0,
            lastAccessed: new Date(),
            completedLessons: [],
            quizScores: {},
          };

          // Update completed lessons if needed
          const completedLessons = [...courseProgress.completedLessons];
          if (progress >= 100 && !completedLessons.includes(lessonId)) {
            completedLessons.push(lessonId);
          }

          // Calculate overall course progress based on completed lessons
          const course = state.enrolledCourses.find(
            (c) => c.courseId === courseId
          );
          let totalLessons = 1;
          if (course) {
            totalLessons = Math.max(course.completedLessons.length, 1);
          }

          const overallProgress = Math.min(
            100,
            Math.floor((completedLessons.length / totalLessons) * 100)
          );

          // Update local state
          set((state) => ({
            progress: {
              ...state.progress,
              courses: {
                ...state.progress.courses,
                [courseId]: {
                  ...courseProgress,
                  progress: overallProgress,
                  lastAccessed: new Date(),
                  completedLessons,
                },
              },
            },
          }));

          // Sync progress with Firestore
          // if (course) {
          //   await syncLearningProgressApi(
          //     authUser._id,
          //     course.id,
          //     get().progress
          //   );
          // }

          // If course is completed, log activity
          if (overallProgress >= 100) {
            await get().logActivity({
              _id: authUser._id,
              type: "course_completed",
              userId: authUser._id,
              timestamp: new Date(),
              metadata: {
                courseId,
              },
            });

            // Update user profile stats
            const profile = get().profile;
            if (profile && profile.learningStats) {
              await get().updateProfile({
                learningStats: {
                  ...profile.learningStats,
                  coursesCompleted: profile.learningStats.coursesCompleted + 1,
                  coursesInProgress: Math.max(
                    0,
                    profile.learningStats.coursesInProgress - 1
                  ),
                },
              });
            }
            // Call checkAndAwardAchievements upon course completion
            await checkAndAwardAchievements();
          }
        } catch (error) {
          console.error("Error updating course progress:", error);
        }
      },

      updateEnrollmentProgress: async (enrollmentId, updates) => {
        try {
          await updateCourseEnrollmentApi(enrollmentId, updates);

          set((state) => ({
            enrolledCourses: state.enrolledCourses.map((enrollment) =>
              enrollment._id === enrollmentId
                ? { ...enrollment, ...updates }
                : enrollment
            ),
          }));
        } catch (error: any) {
          console.error("Error updating enrollment progress:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to update enrollment",
          });
        }
      },

      unenrollFromCourse: async (enrollmentId) => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {
          await unenrollFromCourseApi(enrollmentId);

          set((state) => ({
            enrolledCourses: state.enrolledCourses.filter(
              (enrollment) => enrollment._id !== enrollmentId
            ),
          }));
        } catch (error: any) {
          console.error("Error unenrolling from course:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to unenroll from course",
          });
          throw error; // Re-throw to propagate the error
        }
      },

      loadEnrolledCourses: async () => {
        const authUser = get().authUser;
        if (!authUser?._id) {
          console.log("[UserStore] loadEnrolledCourses: No authenticated user, skipping.");
          set({ enrolledCourses: [] });
          return;
        }

        try {
          const fetched = await fetchEnrolledCoursesApi(authUser._id);

          /**
           * Normalised before it reaches the store, not after. The old code
           * `set({ enrolledCourses })` published the raw response and only then
           * read `.length` — so on a non-array the set had already re-rendered
           * every subscriber with a broken value, and the catch below that
           * resets to `[]` ran too late to prevent the crash.
           */
          const next = Array.isArray(fetched) ? fetched : [];
          /**
           * Keep the previous array when nothing actually changed. Every caller
           * of this published a brand-new reference, so any component with
           * `enrolledCourses` in an effect's dependency list re-ran on a refresh
           * that told it nothing — and one of those effects called back into
           * this, which is how the course catalogue span tens of thousands of
           * requests and killed the tab on mobile.
           */
          const current = get().enrolledCourses;
          const unchanged =
            Array.isArray(current) &&
            current.length === next.length &&
            current.every((entry, i) => sameEnrollment(entry, next[i]));

          set({ enrolledCourses: unchanged ? current : next });
        } catch (error: any) {
          console.error("Error loading enrolled courses:", error);
          set({
            error: error instanceof Error ? error.message : "Failed to load enrolled courses",
            enrolledCourses: [],
          });
        }
      },

      refreshEnrolledCourses: async () => {
        await get().loadEnrolledCourses();
      },

      // Quiz management
      addQuizAttempt: async (attemptData) => {
        const authUser = get().authUser;
        if (!authUser) return null;

        try {
          // Record quiz attempt
          const attempt = await recordQuizAttemptApi(authUser._id, attemptData);

          if (attempt) {
            // Update local state
            set((state) => ({
              quizAttempts: [...state.quizAttempts, attempt],
            }));

            // Update quiz progress
            const quizProgress = get().progress.quizzes[attempt.quizId] || {
              bestScore: 0,
              attempts: 0,
              lastAttempt: new Date(),
              passed: false,
            };

            set((state) => ({
              progress: {
                ...state.progress,
                quizzes: {
                  ...state.progress.quizzes,
                  [attempt.quizId]: {
                    bestScore: Math.max(
                      quizProgress.bestScore,
                      attempt.percentageScore
                    ),
                    attempts: quizProgress.attempts + 1,
                    lastAttempt: new Date(),
                    passed: quizProgress.passed || attempt.passed,
                  },
                },
              },
            }));

            // Log quiz attempt activity
            await get().logActivity({
              type: attempt.passed ? "quiz_passed" : "quiz_attempt",
              userId: authUser._id,
              timestamp: new Date(),
              _id: authUser._id,
              metadata: {
                quizId: attempt.quizId,
                score: attempt.percentageScore,
                passed: attempt.passed,
              },
            });

            // Update profile learningStats for quizzesTaken and averageQuizScore
            const currentProfile = get().profile;
            const allAttempts = [...get().quizAttempts]; // Includes the new attempt already added to state by set() above

            if (currentProfile) {

              console.log("[UserStore.addQuizAttempt] Skipping redundant call to updateProfile for learningStats after quiz attempt.");
            }
          }

          return attempt ?? null;
        } catch (error) {
          console.error("Error adding quiz attempt:", error);
          /**
           * The server's own wording, when it sent one. A refusal here is not
           * always a network fault — a certified course is closed to further
           * attempts, and telling that learner to "check your connection" sends
           * them to fix something that is not broken. Quiz.tsx surfaces this
           * field as a snackbar.
           */
          const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
          if (message) set({ error: message });
          return null;
        }
      },

      removeQuizAttempt: async (attemptId) => {
        try {
          await deleteQuizAttemptApi(attemptId);

          set((state) => ({
            quizAttempts: state.quizAttempts.filter(
              (attempt) => attempt.id !== attemptId
            ),
          }));
        } catch (error) {
          console.error("Error removing quiz attempt:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to remove quiz attempt",
          });
        }
      },

      loadQuizAttempts: async () => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {
          const quizAttempts = await fetchQuizAttemptsApi(authUser._id);
          set({ quizAttempts });
        } catch (error) {
          console.error("Error loading quiz attempts:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to load quiz attempts",
          });
        }
      },

      // Content management
      loadUserCreatedContent: async () => {
        const authUser = get().authUser;
        if (!authUser?._id) return;

        try {
          // const [posts] = await Promise.all([
          //   fetchUserPostsApi(authUser._id),
          // ]);

          set({
            createdContent: {
              // posts: posts,
            },
          });
        } catch (error) {
          console.error("Error loading user created content:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to load created content",
          });
        }
      },



      // Achievement management
      addAchievement: async (achievementData) => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {
          // Check if achievement already exists
          const exists = get().achievements.some(
            (a) => a.name === achievementData.name
          );
          if (exists) return;

          // Add achievement
          const achievement = await addUserAchievementApi(
            authUser._id,
            achievementData
          );

          if (achievement) {
            // Update local state
            set((state) => ({
              achievements: [...state.achievements, achievement],
            }));

            // Add notification for new achievement
            get().sendNotification({
              recipientId: authUser._id,
              _id: authUser._id,
              type: "system",
              content: `You've earned the "${achievement.name}" achievement!`,
              createdAt: new Date(),
              read: false,
              actionLink: "/profile",
            });
          }
        } catch (error) {
          console.error("Error adding achievement:", error);
        }
      },

      loadAchievements: async () => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {
          const achievements = await fetchUserAchievementsApi(authUser._id);
          set({ achievements });
        } catch (error) {
          console.error("Error loading achievements:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to load achievements",
          });
        }
      },

      removeAchievement: async (achievementId) => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {
          await deleteUserAchievementApi(authUser._id, achievementId);

          set((state) => ({
            achievements: state.achievements.filter(
              (achievement) => achievement.id !== achievementId
            ),
          }));
        } catch (error: any) {
          console.error("Error removing achievement:", error);
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to remove achievement",
          });
        }
      },

      recordStudyTime: async (minutes) => {
        const authUser = get().authUser;
        if (!authUser) return;

        try {


          const profile = get().profile;
          if (profile && profile.learningStats) {
            const currentStudyHours = profile.learningStats.studyHours || 0;
            const newStudyHours = currentStudyHours + minutes / 60;

            set({
              profile: {
                ...profile,
                learningStats: {
                  ...profile.learningStats,
                  studyHours: parseFloat(newStudyHours.toFixed(1)),
                  lastStudySession: new Date(),
                },
              },
            });
          }


          // Log study session activity
          await get().logActivity({
            type: "study_session_recorded",
            _id: authUser._id,
            userId: authUser._id,
            timestamp: new Date(),
            metadata: {
              studyMinutes: minutes,
            },
          });

          // Call API to persist study time
          try {
            await recordStudyTimeApi(authUser._id, minutes);
          } catch (apiError) {
            console.error("Failed to sync study time to backend:", apiError);
            // We continue execution as local state is already updated
          }

          // Check for achievements after study time is recorded
          await checkAndAwardAchievements();
        } catch (error) {
          console.error("Error recording study time:", error);
        }
      },

      updatePreferences: async (preferences) => {
        const authUser = get().authUser;
        if (!authUser) return;

        // Kept so a failed save can put it back — see the catch below.
        const previousPreferences = get().preferences;

        try {
          set({ isLoading: true });

          // Update preferences in local state
          const updatedPreferences = {
            ...previousPreferences,
            ...preferences,
          };

          set({ preferences: updatedPreferences });

          // Update preferences via API
          await updatePreferencesApi(authUser._id, preferences);

          // Log preferences update activity
          await get().logActivity({
            type: "profile_updated",
            userId: authUser._id,
            timestamp: new Date(),
            _id: authUser._id,
            metadata: {
              preferencesUpdated: Object.keys(preferences),
            },
          });
        } catch (error) {
          console.error("Error updating preferences:", error);
          /**
           * Roll back and rethrow, for the same reason `updateProfile` does.
           *
           * `SettingsForm.savePreferences` awaits this inside a try/catch that
           * shows "Failed to save preferences" — which could never fire, because
           * swallowing the rejection resolved the promise. The form then set
           * `saveSuccess`, cleared `isDirty` and closed the drawer, leaving the
           * user looking at settings that were never written and would be gone
           * on the next load.
           */
          set({
            preferences: previousPreferences,
            error:
              error instanceof Error
                ? error.message
                : "Unknown error updating preferences",
          });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },


    }),
    {
      name: "fc-user-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Only persist essential data to avoid storage bloat
        authUser: state.authUser,
        profile: state.profile, // UserProfile might also need isSubscribed, subscriptionEndDate
        preferences: state.preferences,
        progress: state.progress,
        isAuthenticated: state.isAuthenticated,
        enrolledCourses: state.enrolledCourses,

      }),
      onRehydrateStorage: () => (state) => {
        // Set loading to false after rehydration
        if (state) {
          state.isLoading = false;
        }
      },
    }
  )
)

export const useAuthUser = () => useUserStore((state) => state.authUser);
export const useUserProfile = () => useUserStore((state) => state.profile);
export const useUserPermissions = () =>
  useUserStore((state) => state.permissions);
export const useUserProgress = () => useUserStore((state) => state.progress);
export const useUserNotifications = () =>
  useUserStore((state) => state.notifications);
export const useUserAchievements = () =>
  useUserStore((state) => state.achievements);
export const useUserPreferences = () =>
  useUserStore((state) => state.preferences);
export const useEnrolledCourses = () =>
  useUserStore((state) => state.enrolledCourses);
export const useCreatedContent = () =>
  useUserStore((state) => state.createdContent);
export const useQuizAttempts = () =>
  useUserStore((state) => state.quizAttempts);
export const useActivityTimeline = () =>
  useUserStore((state) => state.activityTimeline);

export const useUnreadNotifications = () =>
  useUserStore((state) => state.notifications.filter((n) => !n.read));

export const useRecentAchievements = () =>
  useUserStore((state) =>
    state.achievements
      .sort(
        (a, b) =>
          b.earnedAt.getTime() - a.earnedAt.getTime()
      )
      .slice(0, 5)
  );

export const useCompletedCourses = () =>
  useUserStore((state) =>
    state.enrolledCourses.filter(
      (course) => course.status === CourseEnrollmentStatus.COMPLETED
    )
  );

export const useInProgressCourses = () =>
  useUserStore((state) =>
    state.enrolledCourses.filter(
      (course) => course.status === CourseEnrollmentStatus.IN_PROGRESS
    )
  );

export const useUserStats = () => {
  const profile = useUserStore((state) => state.profile);
  const enrolledCourses = useUserStore((state) => state.enrolledCourses);
  const quizAttempts = useUserStore((state) => state.quizAttempts);
  const achievements = useUserStore((state) => state.achievements);
  const createdContent = useUserStore((state) => state.createdContent);

  return useMemo(() => {
    /**
     * Defensive because this runs before any of Profile.tsx's own guards.
     *
     * `Profile.tsx` reads this value as `enrolledCourses?.length` and
     * `(enrolledCourses || [])`, so it already knew the value can be absent —
     * but `useUserStats()` is called first, and it dereferenced it directly.
     * When the store briefly held a non-array the whole profile page threw to a
     * blank screen; Firefox lost that race far more often than the others, so it
     * read as an engine bug rather than the missing guard it was.
     *
     * The two `console.log` calls that used to sit here were the actual throw
     * site, and were shipping the user's enrolment list to the production
     * console besides.
     */
    const courses = Array.isArray(enrolledCourses) ? enrolledCourses : [];
    const attempts = Array.isArray(quizAttempts) ? quizAttempts : [];

    const completedCoursesCount = courses.filter(c => c.status === 'completed').length;
    const inProgressCoursesCount = courses.filter(c => c.status === 'in-progress').length;

    return {
      coursesCompleted: completedCoursesCount,
      coursesInProgress: inProgressCoursesCount,
      totalCoursesEnrolled: courses.length,
      quizzesTaken: attempts.length,
      averageQuizScore: profile?.learningStats?.averageQuizScore || 0,
      studyHours: parseFloat((profile?.learningStats?.studyHours || 0).toFixed(2)),
      streakDays: profile?.learningStats?.streakDays || 0,
      totalAchievements: Array.isArray(achievements) ? achievements.length : 0,
      postsCreated: 0,
    };
  }, [
    profile,
    enrolledCourses,
    quizAttempts,
    achievements,
    createdContent,
  ]);
};

// Action hooks for easier access to actions
export const useUserActions = () => {
  const store = useUserStore();

  return {
    initializeUser: store.initializeUser,
    updateProfile: store.updateProfile,
    updateUserInterests: store.updateUserInterests,
    updateUserRole: store.updateUserRole,
    logActivity: store.logActivity,
    enrollUserInCourse: store.enrollUserInCourse,
    unenrollFromCourse: store.unenrollFromCourse,
    addQuizAttempt: store.addQuizAttempt,
    addAchievement: store.addAchievement,
    recordStudyTime: store.recordStudyTime,
    updatePreferences: store.updatePreferences,
    markNotificationRead: store.markNotificationRead,
    removeNotification: store.removeNotification,
    loadEnrolledCourses: store.loadEnrolledCourses,
    refreshEnrolledCourses: store.refreshEnrolledCourses,

    resetStore: store.resetStore,
    clearErrors: store.clearErrors,
    setLoading: store.setLoading,
  };
};

// Achievement checking utilities
export const checkAndAwardAchievements = async () => {
  const state = useUserStore.getState();
  const profile = state.profile;
  const enrolledCourses = state.enrolledCourses;
  const quizAttempts = state.quizAttempts;
  const createdContent = state.createdContent;
  const achievements = state.achievements;

  if (!profile) return;

  const existingAchievementNames = achievements.map((a) => a.name);

  // achievement criteria
  const achievementCriteria: AchievementCriteria[] = [
    {
      id: "1",
      name: "First Course",
      description: "Enrolled in your first course",
      icon: "🎓",
      type: "course" as const,
      earnedAt: new Date(),
      check: () => enrolledCourses.length >= 1,
    },
    {
      id: "2",
      name: "Course Completionist",
      description: "Completed 5 courses",
      icon: "🏆",
      type: "course" as const,
      earnedAt: new Date(),
      check: () => (profile.learningStats?.coursesCompleted || 0) >= 5,
    },

    {
      id: "3",
      name: "Quiz Master",
      description: "Passed 20 quizzes",
      icon: "🧠",
      type: "quiz" as const,
      earnedAt: new Date(),
      check: () => quizAttempts.filter((q) => q.passed).length >= 20,
    },
    {
      id: "4",
      name: "Perfect Score",
      description: "Achieved 100% on a quiz",
      icon: "⭐",
      type: "quiz" as const,
      earnedAt: new Date(),
      check: () => quizAttempts.some((q) => q.percentageScore === 100),
    },
    {
      id: "5",
      name: "Content Creator",
      description: "Created your first piece of content",
      icon: "✏️",
      type: "contribution" as const,
      earnedAt: new Date(),
      check: () =>
        // createdContent.posts.length > 0 // Modified
        false // No posts functionality
    },
    {
      id: "6",
      name: "Study Streak",
      description: "Maintained a 7-day study streak",
      icon: "🔥",
      type: "engagement" as const,
      earnedAt: new Date(),
      check: () => (profile.learningStats?.streakDays || 0) >= 7,
    },
    {
      id: "7",
      name: "Dedicated Learner",
      description: "Studied for 50+ hours",
      icon: "📚",
      type: "engagement" as const,
      earnedAt: new Date(),
      check: () => (profile.learningStats?.studyHours || 0) >= 50,
    },
    // New Study Hour Achievements
    {
      id: "8",
      name: "Quick Learner",
      description: "Studied for 1 hour",
      icon: "⏱️",
      type: "engagement" as const,
      earnedAt: new Date(),
      check: () => (profile.learningStats?.studyHours || 0) >= 1,
    },
    {
      id: "9",
      name: "Focused Student",
      description: "Studied for 5 hours",
      icon: "🎯",
      type: "engagement" as const,
      earnedAt: new Date(),
      check: () => (profile.learningStats?.studyHours || 0) >= 5,
    },
    {
      id: "10",
      name: "Dedicated Scholar",
      description: "Studied for 10 hours",
      icon: "🧑‍🎓",
      type: "engagement" as const,
      earnedAt: new Date(),
      check: () => (profile.learningStats?.studyHours || 0) >= 10,
    },
    {
      id: "11",
      name: "Knowledge Seeker",
      description: "Studied for 25 hours",
      icon: "💡",
      type: "engagement" as const,
      earnedAt: new Date(),
      check: () => (profile.learningStats?.studyHours || 0) >= 25,
    },
    {
      id: "12",
      name: "Learning Machine",
      description: "Studied for 100 hours",
      icon: "🤖",
      type: "engagement" as const,
      earnedAt: new Date(),
      check: () => (profile.learningStats?.studyHours || 0) >= 100,
    },
  ];

  // Check and award new achievements
  for (const criteria of achievementCriteria) {
    if (!existingAchievementNames.includes(criteria.name) && criteria.check()) {
      await state.addAchievement({
        name: criteria.name,
        description: criteria.description,
        icon: criteria.icon,
        type: criteria.type,
      });
    }
  }
};

export default useUserStore;